/**
 * Helper functions for extracting and handling API error messages
 */

import { isUnreachableError } from '../services/connectionStatus';

interface ApiErrorData {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  title?: string;
  detail?: string;
  Message?: string; // PascalCase version
  Error?: string;
}

interface FetchBaseQueryError {
  status: number | 'FETCH_ERROR' | 'TIMEOUT_ERROR' | 'PARSING_ERROR' | 'CUSTOM_ERROR';
  data?: unknown;
  error?: string;
}

/**
 * Mensaje para una escritura que lleva Id del cliente (crear orden, comandear, cobrar).
 * Cuando no hubo respuesta del servidor ya se reintentó sola (ver baseApi), y lo que la
 * cajera necesita saber es que volver a pulsar es seguro: sin esto dudaba entre
 * reintentar y arriesgarse a cobrar doble, o dejar la mesa sin cobrar.
 */
export function getRetryableWriteErrorMessage(error: unknown, defaultMessage: string): string {
  if (typeof error === 'object' && error !== null && 'status' in error
      && isUnreachableError(error as { status: unknown })) {
    return 'No hubo respuesta del servidor. Revisa la conexión y vuelve a intentar: no se duplicará.';
  }
  return getErrorMessage(error, defaultMessage);
}

/**
 * Extracts a user-friendly error message from an RTK Query error
 * Handles various error response formats from the API
 */
export function getErrorMessage(error: unknown, defaultMessage = 'Ha ocurrido un error'): string {
  if (!error) {
    return defaultMessage;
  }

  // Los fallos de transporte se resuelven ANTES del bloque de abajo, que está
  // guardado por `'data' in error`. fetchBaseQuery construye estos errores como
  // `{ status: 'FETCH_ERROR' | 'TIMEOUT_ERROR', error: String(e) }` — sin clave
  // `data`—, así que el guard los saltaba enteros y el caso de red que se manejaba
  // más abajo era inalcanzable: un wifi caído a media comanda terminaba mostrando
  // el mensaje default en vez de decir que revisaras la conexión.
  if (typeof error === 'object' && 'status' in error) {
    const transportStatus = (error as FetchBaseQueryError).status;
    if (transportStatus === 'FETCH_ERROR') {
      return 'Error de conexión. Verifique su conexión a internet.';
    }
    if (transportStatus === 'TIMEOUT_ERROR') {
      return 'La solicitud tardó demasiado. Verifique su conexión e intente de nuevo.';
    }
  }

  // Handle RTK Query FetchBaseQueryError
  if (typeof error === 'object' && 'data' in error) {
    const fetchError = error as FetchBaseQueryError;
    const data = fetchError.data as ApiErrorData | string;

    // If data is a string, return it directly
    if (typeof data === 'string') {
      return data || defaultMessage;
    }

    // If data is an object, try to extract message
    if (data && typeof data === 'object') {
      // Check for common error message properties (both camelCase and PascalCase)
      if (data.message) {
        return data.message;
      }
      if (data.Message) {
        return data.Message;
      }
      if (data.error) {
        return data.error;
      }
      if (data.Error) {
        return data.Error;
      }
      if (data.detail) {
        return data.detail;
      }
      // Los errores de validación van ANTES de `title`. Los controladores son
      // [ApiController], así que un DTO inválido responde ValidationProblemDetails,
      // que trae AMBOS: un `title` genérico en inglés ("One or more validation
      // errors occurred.") y el `errors` con los mensajes de campo reales. Mirando
      // `title` primero, la cajera veía la frase genérica sin traducir en vez de
      // "At least one payment method is required".
      if (data.errors) {
        const firstError = Object.values(data.errors).flat()[0];
        if (firstError) {
          return firstError;
        }
      }
      if (data.title) {
        return data.title;
      }

      // Try to stringify the object if we couldn't find a known property
      const dataStr = JSON.stringify(data);
      if (dataStr && dataStr !== '{}') {
        // Check if it looks like an error message
        const anyData = data as Record<string, unknown>;
        const possibleMessage = Object.values(anyData).find(v => typeof v === 'string' && v.length > 0);
        if (possibleMessage && typeof possibleMessage === 'string') {
          return possibleMessage;
        }
      }
    }

    // If there's an error property at the root level
    if (fetchError.error) {
      return fetchError.error;
    }

    // Handle specific HTTP status codes
    if (typeof fetchError.status === 'number') {
      switch (fetchError.status) {
        case 400:
          return 'Solicitud incorrecta. Verifique los datos ingresados.';
        case 401:
          return 'No autorizado. Por favor inicie sesión nuevamente.';
        case 403:
          return 'No tiene permisos para realizar esta acción.';
        case 404:
          return 'El recurso solicitado no fue encontrado.';
        case 500:
          return 'Error interno del servidor. Por favor intente más tarde.';
        case 502:
        case 503:
        case 504:
          return 'El servicio no está disponible. Por favor intente más tarde.';
      }
    }

    // Handle network errors
    if (fetchError.status === 'FETCH_ERROR') {
      return 'Error de conexión. Verifique su conexión a internet.';
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return defaultMessage;
}

/**
 * Checks if an error is a network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('status' in error) {
    const status = (error as FetchBaseQueryError).status;
    return status === 'FETCH_ERROR';
  }

  return false;
}

/**
 * Checks if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('status' in error) {
    const status = (error as FetchBaseQueryError).status;
    return status === 401 || status === 403;
  }

  return false;
}
